import {
  AiCompanionReviewFieldStateEnum,
  AiCompanionReviewModeEnum,
  AiCompanionTrainingFieldEnum,
  type AiCompanionReviewValueModel,
  type AiCompanionTrainingReviewModel,
} from "@simbelapp/order-sdk";
import { TrainingPrefillFieldEnum } from "~/utils/enums/training-prefill.enums";
import type {
  ICopilotMatchedTraining,
  ICopilotReviewMatchSegment,
  ICopilotTrainingReview,
} from "~/utils/interfaces/copilot-interfaces";
import type { ITrainingPrefill, ITrainingPrefillValues } from "~/utils/interfaces/training-prefill-interfaces";

/** States meaning "the assistant put this here" — they drive the ✦ marker. */
export const AI_FILLED_STATES: AiCompanionReviewFieldStateEnum[] = [
  AiCompanionReviewFieldStateEnum.Extracted,
  AiCompanionReviewFieldStateEnum.Changed,
];

/**
 * Wire ids → form keys (same values). Partial: a field the backend added but the form does not
 * pre-fill yet is skipped by the `!field` guard in `trainingPrefillOf`, not injected.
 */
export const PREFILL_FIELD_OF: Partial<Record<AiCompanionTrainingFieldEnum, TrainingPrefillFieldEnum>> = {
  [AiCompanionTrainingFieldEnum.TrainingPartner]: TrainingPrefillFieldEnum.TRAINING_PARTNER,
  [AiCompanionTrainingFieldEnum.Title]: TrainingPrefillFieldEnum.TITLE,
  [AiCompanionTrainingFieldEnum.ExternalUrl]: TrainingPrefillFieldEnum.EXTERNAL_URL,
  [AiCompanionTrainingFieldEnum.DurationHours]: TrainingPrefillFieldEnum.DURATION_HOURS,
  [AiCompanionTrainingFieldEnum.DurationDays]: TrainingPrefillFieldEnum.DURATION_DAYS,
  [AiCompanionTrainingFieldEnum.TargetAudience]: TrainingPrefillFieldEnum.TARGET_AUDIENCE,
  [AiCompanionTrainingFieldEnum.Goals]: TrainingPrefillFieldEnum.GOALS,
  [AiCompanionTrainingFieldEnum.Program]: TrainingPrefillFieldEnum.PROGRAM,
  [AiCompanionTrainingFieldEnum.Format]: TrainingPrefillFieldEnum.FORMAT,
  [AiCompanionTrainingFieldEnum.PriceInterExclTax]: TrainingPrefillFieldEnum.PRICE_INTER_EXCL_TAX,
  [AiCompanionTrainingFieldEnum.PriceIntraExclTax]: TrainingPrefillFieldEnum.PRICE_INTRA_EXCL_TAX,
};

const HTML_TAG_REGEX = /<\/?[a-z][^>]*>/i;

/**
 * The extraction keeps the document's line breaks as plain text, but the form's program field is
 * a rich-text editor where a bare newline collapses: one escaped paragraph per line.
 */
export function trainingProgramHtmlOf(program: string): string {
  if (HTML_TAG_REGEX.test(program)) return program;

  return program
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `<p>${line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`)
    .join("");
}

/** Which slot of a review value feeds each form key (`field` tells which one is filled). */
export const TRAINING_PREFILL_VALUE_WRITERS: Record<
  TrainingPrefillFieldEnum,
  (values: ITrainingPrefillValues, value: AiCompanionReviewValueModel | null) => void
> = {
  [TrainingPrefillFieldEnum.TRAINING_PARTNER]: (values, value) => {
    values.providerName = value?.text ?? null;
  },
  [TrainingPrefillFieldEnum.TITLE]: (values, value) => {
    values.title = value?.text ?? null;
  },
  [TrainingPrefillFieldEnum.EXTERNAL_URL]: (values, value) => {
    values.externalUrl = value?.text ?? null;
  },
  [TrainingPrefillFieldEnum.DURATION_HOURS]: (values, value) => {
    values.durationHours = value?.number ?? null;
  },
  [TrainingPrefillFieldEnum.DURATION_DAYS]: (values, value) => {
    values.durationDays = value?.number ?? null;
  },
  [TrainingPrefillFieldEnum.TARGET_AUDIENCE]: (values, value) => {
    values.targetAudience = value?.text ?? null;
  },
  [TrainingPrefillFieldEnum.GOALS]: (values, value) => {
    values.goals = value?.list ?? [];
  },
  [TrainingPrefillFieldEnum.PROGRAM]: (values, value) => {
    values.program = value?.text ? trainingProgramHtmlOf(value.text) : null;
  },
  [TrainingPrefillFieldEnum.FORMAT]: (values, value) => {
    values.format = value?.text ?? null;
  },
  [TrainingPrefillFieldEnum.PRICE_INTER_EXCL_TAX]: (values, value) => {
    values.priceInterExclTax = value?.number ?? null;
  },
  [TrainingPrefillFieldEnum.PRICE_INTRA_EXCL_TAX]: (values, value) => {
    values.priceIntraExclTax = value?.number ?? null;
  },
};

/** Reads the review lines into the form's shape: only the AI-filled fields, each from its typed slot. */
export function trainingPrefillOf(review: AiCompanionTrainingReviewModel): ITrainingPrefill {
  const values: ITrainingPrefillValues = {};
  const fields: TrainingPrefillFieldEnum[] = [];

  for (const line of review.fields) {
    const field = PREFILL_FIELD_OF[line.field];
    if (!field || !AI_FILLED_STATES.includes(line.state)) continue;

    TRAINING_PREFILL_VALUE_WRITERS[field](values, line.proposedValue ?? null);
    fields.push(field);
  }

  return { trainingId: review.trainingId, mode: review.mode, document: review.document ?? null, values, fields };
}

/**
 * Whether a URL the admin submitted may be sent for analysis. Only http(s): the same string is
 * shown in the program viewer, where any other scheme would run in the app's own origin.
 */
export function isImportableProgramUrl(url: string): boolean {
  try {
    const parsed = new URL(url);

    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Which catalog training an update was matched to — `null` on a create, and on an update the
 * payload does not name. Nothing else in the review identifies it: an import matches on an
 * identical title, so that title is UNCHANGED and the diff drops it.
 */
export function matchedTrainingOf(review: ICopilotTrainingReview): ICopilotMatchedTraining | null {
  if (review.mode !== AiCompanionReviewModeEnum.Update) return null;

  const title = review.matchedTraining?.title?.trim();
  if (!title) return null;

  return { title, providerName: review.matchedTraining?.providerName?.trim() || null };
}

/** How the identity line opens; what follows it is the fiche, and the fiche alone is emphasised. */
export const TRAINING_REVIEW_MATCH_LEAD = "Cette formation semble déjà présente dans le catalogue :";

/**
 * The line that opens an update: which fiche the assistant recognised, named the way the catalog
 * names it. It is what makes a wrong match visible — an import the admin meant as a new training,
 * or a homonym — before the CTA opens that fiche.
 *
 * Cut into runs rather than returned as one string because the title and the provider are emphasised
 * and the rest is not. Each run carries its own spacing: Vue drops a whitespace-only text node that
 * spans a line break, so a template composing this sentence across several elements would lose its
 * spaces the first time it is reformatted — silently, and only in the rendered output.
 */
export function trainingReviewMatchSegmentsOf(review: ICopilotTrainingReview): ICopilotReviewMatchSegment[] | null {
  const matched = matchedTrainingOf(review);
  if (!matched) return null;

  return [
    { text: `${TRAINING_REVIEW_MATCH_LEAD} `, strong: false },
    { text: matched.title, strong: true },
    ...(matched.providerName
      ? [
          { text: " — ", strong: false },
          { text: matched.providerName, strong: true },
        ]
      : []),
    { text: ".", strong: false },
  ];
}

/**
 * How the answer signs off: what the CTA is about to do, in the admin's own terms. The fields
 * themselves are not listed — they are what the form opens on, and the backend's recap of them —
 * the whole program, verbatim — is dropped for this very reason.
 *
 * A read that found nothing still has to say so rather than hand over an empty form silently. On an
 * update that means the document changes nothing: the review is a diff, so an empty one is an
 * answer about the fiche, not about the program.
 */
export function trainingReviewSummaryOf(review: AiCompanionTrainingReviewModel): string {
  const hasFilledField = review.fields.some((field) => AI_FILLED_STATES.includes(field.state));
  const isUpdate = review.mode === AiCompanionReviewModeEnum.Update;

  if (!hasFilledField) {
    /*
     * On an update, an empty line-up is ambiguous: the review is a diff, and a document that changes
     * nothing and a document nothing could be read from both arrive as `fields: []` — every field is
     * MISSING, which update mode filters out exactly as it filters UNCHANGED. So the card states
     * what is certain, that no modification was prepared, rather than diagnosing which happened.
     */
    return isUpdate
      ? "Aucune modification n'a été préparée à partir de ce programme."
      : "Aucun champ n'a pu être lu dans le programme.";
  }

  return isUpdate
    ? "La fiche existante peut être mise à jour avec les informations de ce programme."
    : "Fiche formation préparée à partir du programme.";
}

/** The CTA names what the form will open on: a fresh draft, or a diff over a stored training. */
export function trainingReviewOpenLabelOf(review: AiCompanionTrainingReviewModel): string {
  return review.mode === AiCompanionReviewModeEnum.Update ? "Mettre à jour la fiche" : "Ouvrir la fiche pré-remplie";
}

/**
 * The banner above the pre-filled form. Update mode names the proposals; create mode points at the
 * document when the viewer shows it, and only names the marked fields when it does not.
 */
export function trainingPrefillNoticeOf(
  mode: AiCompanionReviewModeEnum | null | undefined,
  viewerOpen: boolean,
): string {
  if (mode === AiCompanionReviewModeEnum.Update) {
    return "L'IA propose des modifications à partir du programme sur les champs signalés. Vérifiez-les avant d'enregistrer.";
  }

  return viewerOpen
    ? "L'IA a pré-rempli les champs signalés à partir du document affiché. Vérifiez-les avant d'enregistrer."
    : "Champs signalés : pré-remplis par l'IA. Vérifiez-les avant d'enregistrer.";
}
